import React, { useMemo, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import { useFieldArray, useForm } from 'react-hook-form';
import {
  createLogEntryPayload,
  withTrackProps,
} from '@dpdgroupuk/react-event-tracker';

import RightDrawerModal from '../../RightDrawerModal';
import SliderCard from '../../SliderCard';
import Button from '../../Button';
import palette from '../../styles/palette';
import CriteriaItem from './CriteriaItem';
import {
  isButtonsEnabled,
  getDefaultCriteriaItem,
  getCriteriaDiff,
} from './model';
import {
  SELECT_BY_CRITERIA_TITLE,
  CLEAR_ALL_CRITERIA,
  APPLY_CRITERIA,
  ADD_NEW_CRITERIA,
} from '../../../constants/strings';
import { SELECT_STOP_BY_CRITERIA } from '../../../constants/analytics';

const useStyles = makeStyles(({ typography }) => ({
  applyButton: {
    marginLeft: typography.pxToRem(20),
  },
}));

const SelectByCriteriaModal = ({
  open,
  onClose,
  route,
  onSave,
  criteria,
  handleClearClickIds,
  handleAddCriteriaIds,
  handleRemoveCriteriaIds,
  handleSelectCriteriaIds,
}) => {
  const styles = useStyles();

  const initialValues = useMemo(
    () => ({
      criteria: criteria?.length ? criteria : [getDefaultCriteriaItem()],
    }),
    [criteria]
  );

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { isValid },
  } = useForm({
    defaultValues: initialValues,
    mode: 'onChange',
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'criteria',
  });

  const isFormDirty = useCallback(
    (data) => getCriteriaDiff(data, initialValues.criteria).length,
    [initialValues.criteria]
  );

  const handleClearClick = useCallback(() => {
    handleClearClickIds();
    reset({ criteria: [getDefaultCriteriaItem()] });
  }, [handleClearClickIds, reset]);

  const handleAddNewCriteria = useCallback(() => {
    handleAddCriteriaIds();
    append(getDefaultCriteriaItem());
  }, [handleAddCriteriaIds, append]);

  const handleRemoveCriteria = useCallback(
    (index) => {
      handleRemoveCriteriaIds(index);
      remove(index);
    },
    [handleRemoveCriteriaIds, remove]
  );

  const handleChangeCriteriaValue = useCallback(
    (idx, fieldName, value) => {
      const currentValues = getValues('criteria');
      const criteria = currentValues[idx];
      const updatedCriteria =
        fieldName === 'criteria' ? value : { ...criteria, [fieldName]: value };
      handleSelectCriteriaIds(updatedCriteria);
      update(idx, updatedCriteria);
    },
    [handleSelectCriteriaIds, update]
  );

  return (
    <RightDrawerModal
      open={open}
      onClose={onClose}
      title={SELECT_BY_CRITERIA_TITLE}
      loadId={SELECT_STOP_BY_CRITERIA.LOAD}
      actions={
        <>
          <Button
            variant="contained"
            bgColor={palette.yellow}
            color={palette.typography.black.high}
            colorDisabled={palette.typography.black.disabled}
            border={`1px solid ${palette.map.key3}`}
            onClick={handleClearClick}
            bgColorDisabled="rgba(255,213,0, .38)"
            disabled={!isButtonsEnabled(fields)}
          >
            {CLEAR_ALL_CRITERIA}
          </Button>
          <Button
            variant="contained"
            bgColor={palette.map.key2}
            border={'1px solid rgba(0, 96, 162, .38)'}
            className={styles.applyButton}
            onClick={handleSubmit(onSave)}
            disabled={!isFormDirty(fields) || !isValid}
            bgColorDisabled="rgba(68,167,223, .38)"
          >
            {APPLY_CRITERIA}
          </Button>
        </>
      }
    >
      <SliderCard title={ADD_NEW_CRITERIA}>
        <form style={{ width: '100%' }}>
          {fields.map((criteriaItem, index, allCriteria) => (
            <CriteriaItem
              key={criteriaItem.id}
              onAddClick={handleAddNewCriteria}
              onRemoveClick={() => handleRemoveCriteria(index)}
              prefixName={`criteria[${index}]`}
              route={route}
              value={criteriaItem}
              // onValueChange={handleChangeCriteriaValue}
              onValueChange={(fieldName, value) =>
                handleChangeCriteriaValue(index, fieldName, value)
              }
              last={index === allCriteria.length - 1}
              first={index === 0}
              control={control}
            />
          ))}
        </form>
      </SliderCard>
    </RightDrawerModal>
  );
};

export default withTrackProps({
  handleClearClickIds: SELECT_STOP_BY_CRITERIA.SELECT_CLEAR_ALL_CRITERIA,
  handleAddCriteriaIds: SELECT_STOP_BY_CRITERIA.SELECT_ADD,
  handleRemoveCriteriaIds: SELECT_STOP_BY_CRITERIA.SELECT_REMOVE,
  onSave: SELECT_STOP_BY_CRITERIA.SELECT_APPLY_CRITERIA,
  handleSelectCriteriaIds: (value) =>
    createLogEntryPayload(SELECT_STOP_BY_CRITERIA.SELECT_CRITERIA, {
      value,
    }),
})(React.memo(SelectByCriteriaModal));
